import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, InputNumber, DatePicker,Button, message } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { ModeloCampanha } from "../../../services/modeloCampanhasService";
import { useSeletoresDestinatarios } from "../hooks/useSeletoresDestinatarios";
import { useCategoriasMensagem } from "../hooks/useCategoriasMensagem";

const { TextArea } = Input;

interface ModalEditarModeloCampanhaProps {
  open: boolean;
  onClose: () => void;
  modeloCampanha: ModeloCampanha | null;
  onSubmit: (id: number, dados: Partial<ModeloCampanha>) => Promise<boolean>;
}

export const ModalEditarModeloCampanha: React.FC<ModalEditarModeloCampanhaProps> = ({
  open,
  onClose,
  modeloCampanha,
  onSubmit,
}) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  // 🔹 Hooks para carregar seletores e categorias
  const { seletores, loading: loadingSeletores } = useSeletoresDestinatarios();
  const { categorias, loading: loadingCategorias } = useCategoriasMensagem();

  // 🔹 Preenche o formulário ao abrir
  useEffect(() => {
    if (modeloCampanha) {
      form.setFieldsValue({
        nome: modeloCampanha.nome,
        descricao: modeloCampanha.descricao,
        prioridade: modeloCampanha.prioridade ?? 10,
        frequenciaDias: modeloCampanha.frequenciaDias ?? undefined,
        seletorId: modeloCampanha.seletorId ?? undefined,
        categoriaMensagemId: modeloCampanha.categoriaMensagemId ?? undefined,
        parametroSeletor: modeloCampanha.parametroSeletor
          ? JSON.stringify(modeloCampanha.parametroSeletor, null, 2)
          : "",
           proximaExecucao: modeloCampanha.proximaExecucao
          ? dayjs(modeloCampanha.proximaExecucao)
          : dayjs(), // default: hoje
          limiteMensal: modeloCampanha.limiteMensal ?? undefined,
      });
    } else {
      form.resetFields();
    }
  }, [modeloCampanha, form]);

  // 🔹 Submeter edição
  const handleSubmit = async () => {
  try {
    const values = await form.validateFields();
    if (!modeloCampanha) return;

    setLoading(true);

    const payload = {
      nome: values.nome,
      descricao: values.descricao,
      seletorId: values.seletorId || null,
      parametroSeletor: values.parametroSeletor
        ? JSON.parse(values.parametroSeletor)
        : null,
      prioridade: values.prioridade ?? 10,
      frequenciaDias: values.frequenciaDias,
      categoriaMensagemId: values.categoriaMensagemId,
      proximaExecucao: values.proximaExecucao
          ? values.proximaExecucao.toISOString()
          : undefined,
      limiteMensal: values.limiteMensal,
    };

   

    // 🔹 Aguarda o retorno boolean do pai
    const sucesso = await onSubmit(modeloCampanha.id, payload);

    if (sucesso) {
      // message.success("Modelo de campanha atualizado com sucesso!");
      form.resetFields();
      onClose(); // ✅ fecha modal aqui, só após retorno positivo
    } else {
      message.error("Erro ao atualizar modelo de campanha.");
    }
  } catch (error) {
    console.error("Erro ao salvar alterações:", error);
    message.error("Erro ao salvar alterações.");
  } finally {
    // ✅ garante que o botão volta ao normal
    setLoading(false);
  }
};


  return (
    <Modal
      title="Editar Modelo de Campanha"
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {/* Nome */}
        <Form.Item
          label="Nome"
          name="nome"
          rules={[{ required: true, message: "Informe o nome do modelo" }]}
        >
          <Input placeholder="Digite o nome do modelo de campanha" />
        </Form.Item>

        {/* Descrição */}
        <Form.Item
          label="Descrição"
          name="descricao"
          rules={[{ required: true, message: "Informe a descrição" }]}
        >
          <TextArea rows={3} placeholder="Descrição do modelo" />
        </Form.Item>

        {/* Seletor */}
        <Form.Item
          label="Seletor"
          name="seletorId"
          rules={[{ required: true, message: "Selecione um seletor" }]}
        >
          <Select
            placeholder="Selecione o seletor"
            loading={loadingSeletores}
            allowClear
          >
            {seletores.map((sel) => (
              <Select.Option key={sel.id} value={sel.id}>
                {sel.nome}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Categoria Mensagem */}
        <Form.Item
          label="Categoria de Mensagem"
          name="categoriaMensagemId"
          rules={[{ required: true, message: "Selecione a categoria de mensagem" }]}
        >
          <Select
            placeholder="Selecione a categoria"
            loading={loadingCategorias}
            allowClear
          >
            {categorias.map((cat) => (
              <Select.Option key={cat.id} value={cat.id}>
                {cat.nome}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Parâmetro Seletor */}
        <Form.Item
          label="Parâmetro do Seletor (JSON)"
          name="parametroSeletor"
          tooltip="Exemplo: { 'etiquetaId': 1, 'periodoMin': 450 }"
        >
          <TextArea rows={3} placeholder="Informe parâmetros em formato JSON (opcional)" />
        </Form.Item>

        {/* Prioridade */}
        <Form.Item
          label="Prioridade (1 a 10)"
          name="prioridade"
          tooltip="1 = mais alta, 10 = mais baixa"
        >
          <InputNumber min={1} max={10} style={{ width: "100%" }} />
        </Form.Item>

        {/* Frequência */}
        <Form.Item
          label="Frequência (em dias)"
          name="frequenciaDias"
          rules={[
            { required: true, message: "Informe a frequência em dias" },
            {
              validator(_, value) {
                if (!value || value > 0) return Promise.resolve();
                return Promise.reject(new Error("A frequência deve ser maior que zero"));
              },
            },
          ]}
        >
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          label="Limite mensal"
          name="limiteMensal"
          rules={[
            { required: true, message: "Informe o limite mensal" },
            { validator(_, value) {
                if (value === undefined || value >= 0) return Promise.resolve();
                return Promise.reject(new Error("O limite deve ser zero ou maior"));
              }
            }
          ]}
        >
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>

          <Form.Item
          label="Próxima Execução"
          name="proximaExecucao"
          rules={[
            {
              validator(_, value) {
                if (!value || value.isAfter(dayjs().subtract(1, "minute"))) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("A data de execução deve ser igual ou futura")
                );
              },
            },
          ]}
        >
          <DatePicker
            showTime
            format="DD/MM/YYYY HH:mm"
            style={{ width: "100%" }}
          />
        </Form.Item>


        {/* Botão de salvar */}
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={loading}
            block
          >
            Salvar Alterações
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};
